/**
 * EmptyVisual — the one primitive every empty surface renders through.
 *
 * Flat Grammar 2027: an empty state is DRAWN, not explained. A glyph
 * composition carries the meaning — either an icon in a tonal circle, or a
 * shape hint that previews what will live there — plus one line of six words
 * or fewer, at most one muted hint line (the next step, e.g. "Ask an admin if
 * you need it."), and at most one action. Never a paragraph.
 *
 * The shapes are pure CSS (see `styles/empty-visual.css`): tonal fills, no
 * borders, no shadows, tokens only, and a 1.2s opacity breathe that stops for
 * `prefers-reduced-motion`.
 */

import type { ComponentType, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { LockIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import { Link } from 'react-router-dom'

import '@/styles/empty-visual.css'

/** Loose enough to take a Phosphor or a Lucide icon component. */
export type EmptyVisualIcon = ComponentType<{ className?: string }>

/**
 * `icon` falls back to a tonal circle; every other value previews the content
 * that will land here once the surface fills up.
 */
export type EmptyVisualShape =
  | 'rows'
  | 'bars'
  | 'grid'
  | 'calendar'
  | 'avatars'
  | 'icon'

/** page = centred and roomy · card = compact in-card · strip = one inline line */
export type EmptyVisualSize = 'page' | 'card' | 'strip'

/** quiet = nothing yet · filtered = the filters hid it · locked = no access */
export type EmptyVisualTone = 'quiet' | 'filtered' | 'locked'

export interface EmptyVisualAction {
  label: string
  onClick?: () => void
  /** Router destination — takes over when there is no `onClick`. */
  href?: string
  icon?: EmptyVisualIcon
  disabled?: boolean
}

export interface EmptyVisualProps {
  shape?: EmptyVisualShape
  /** Drawn inside the circle for `shape="icon"`, or as the corner glyph. */
  icon?: EmptyVisualIcon
  /** The whole copy budget: six words or fewer. */
  line: string
  /** One muted line under `line` for the next step. Optional, never a paragraph. */
  hint?: string
  action?: EmptyVisualAction
  size?: EmptyVisualSize
  tone?: EmptyVisualTone
  /** Stretches a page-size empty to fill the viewport (route-level denials). */
  fullScreen?: boolean
  className?: string
}

/** The copy budget from the grammar doc, enforced loudly in dev only. */
const MAX_WORDS = 6

function warnIfWordy(line: string) {
  if (!import.meta.env.DEV) return
  const words = line.trim().split(/\s+/).filter(Boolean)
  if (words.length > MAX_WORDS) {
    console.warn(
      `[EmptyVisual] "${line}" is ${words.length} words — the empty-state line budget is ${MAX_WORDS}.`,
    )
  }
}

function ghost(className: string, key?: string | number) {
  return <span key={key} className={cn('ev-ghost', className)} />
}

function ShapeHint({ shape }: { shape: Exclude<EmptyVisualShape, 'icon'> }) {
  switch (shape) {
    case 'rows':
      return (
        <>
          {[0, 1, 2].map(index => (
            <span key={index} className="ev-row">
              {ghost('ev-row-dot')}
              {ghost('ev-row-bar')}
            </span>
          ))}
        </>
      )
    case 'bars':
      return <>{[0, 1, 2].map(index => ghost('ev-bar', index))}</>
    case 'grid':
      return (
        <>{Array.from({ length: 9 }, (_, index) => ghost('ev-cell', index))}</>
      )
    case 'calendar':
      return (
        <>
          {ghost('ev-cal-head')}
          <span className="ev-cal-grid">
            {Array.from({ length: 28 }, (_, index) =>
              ghost('ev-cal-cell', index),
            )}
          </span>
        </>
      )
    case 'avatars':
      return <>{[0, 1, 2].map(index => ghost('ev-av', index))}</>
  }
}

const SHAPE_CLASS: Record<Exclude<EmptyVisualShape, 'icon'>, string> = {
  rows: 'ev-rows',
  bars: 'ev-bars',
  grid: 'ev-grid',
  calendar: 'ev-cal',
  avatars: 'ev-avatars',
}

export function EmptyVisual({
  shape = 'icon',
  icon,
  line,
  hint,
  action,
  size = 'page',
  tone = 'quiet',
  fullScreen = false,
  className,
}: EmptyVisualProps) {
  warnIfWordy(line)

  // Tone owns the glyph when it has something to say — a filtered list and a
  // locked one are the same shape with a different mark.
  const Glyph: EmptyVisualIcon | undefined =
    tone === 'locked'
      ? LockIcon
      : tone === 'filtered'
        ? MagnifyingGlassIcon
        : icon

  const visual =
    size === 'strip' ? (
      <span className="ev-mark" aria-hidden>
        {Glyph ? (
          <Glyph className="h-3.5 w-3.5" />
        ) : (
          <span className="ev-mark-bars">
            <span className="ev-mark-bar" />
            <span className="ev-mark-bar" />
          </span>
        )}
      </span>
    ) : shape === 'icon' ? (
      <span className="ev-shape" aria-hidden>
        <span className="ev-circle">
          {Glyph && (
            <Glyph className={size === 'card' ? 'h-4 w-4' : 'h-5 w-5'} />
          )}
        </span>
      </span>
    ) : (
      <span className={cn('ev-shape', SHAPE_CLASS[shape])} aria-hidden>
        <ShapeHint shape={shape} />
        {Glyph && (
          <span className="ev-glyph">
            <Glyph className={size === 'card' ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
          </span>
        )}
      </span>
    )

  return (
    <div
      className={cn(
        'ev',
        size === 'page' && 'ev-page',
        size === 'card' && 'ev-card',
        size === 'strip' && 'ev-strip',
        fullScreen && size === 'page' && 'ev-fullscreen',
        className,
      )}
    >
      {visual}
      <p className="ev-line">{line}</p>
      {hint && <p className="ev-hint">{hint}</p>}
      {action && <EmptyVisualButton action={action} />}
    </div>
  )
}

function EmptyVisualButton({ action }: { action: EmptyVisualAction }) {
  const ActionIcon = action.icon
  const content: ReactNode = (
    <>
      {ActionIcon && <ActionIcon className="h-3.5 w-3.5" />}
      {action.label}
    </>
  )

  if (!action.onClick && action.href) {
    return (
      <Link to={action.href} className="ev-action">
        {content}
      </Link>
    )
  }

  return (
    <button
      type="button"
      className="ev-action"
      disabled={action.disabled}
      onClick={action.onClick}
    >
      {content}
    </button>
  )
}
